"use client";

import { useCallback, useMemo, useState } from "react";
import { type Option, PillGroup } from "@/components/tool-kit/primitives";
import {
  SourceCitationBuilder,
  type SourceField,
  type SourceValues,
} from "@/components/tool-kit/source-citation-builder";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  type AnnualReportLabels,
  annualReportIsReady,
  ANNUAL_REPORT_FIELDS as F,
  formatAnnualReport,
  type ReportKind,
} from "@/lib/tool-kit/citation-sources-books/annual-report-citation-generator";

/** English strings. A translated content file overrides these through `ui`. */
export const ANNUAL_REPORT_CITATION_GENERATOR_STRINGS = {
  formHeading: "Report details",
  resultsHeading: "Your citations",
  emptyPrompt: "Enter the company to see your citations.",
  copy: "Copy",
  copied: "Copied!",
  tryExample: "Try an example",
  clear: "Clear",
  kindLabel: "What are you citing?",
  kindAnnual: "A company's annual report",
  kindSec: "An SEC filing (10-K, 10-Q…)",
  formLabel: "Which filing?",
  form10k: "Form 10-K (annual report)",
  form10q: "Form 10-Q (quarterly report)",
  form8k: "Form 8-K (current report)",
  form20f: "Form 20-F (foreign company annual report)",
  companyLabel: "Company",
  companyPlaceholder: "Apple Inc.",
  companyHint:
    "The registrant's name as the filing prints it, with Inc. or PLC.",
  titleAnnualLabel: "Title of the report",
  titleAnnualPlaceholder: "Microsoft annual report 2023",
  titleSecLabel: "Title (optional)",
  titleSecPlaceholder: "Leave blank to use the form and period",
  titleHint:
    "Sentence case. APA and Harvard keep it; MLA and Chicago get title case automatically.",
  periodLabel: "Period covered (fiscal year or quarter end)",
  periodPlaceholder: "2023-09-30",
  dateAnnualLabel: "Year published",
  dateSecLabel: "Date filed with the SEC",
  datePlaceholder: "YYYY-MM-DD",
  publisherLabel: "Publisher (optional)",
  publisherPlaceholder: "Leave blank for the SEC, or the company itself",
  urlLabel: "URL",
  urlPlaceholder: "https://www.sec.gov/Archives/edgar/data/…",
  accessedLabel: "Date you viewed it (Harvard, MLA)",
  accessedPlaceholder: "YYYY-MM-DD",
  pageLabel: "Page you cite (optional)",
  pagePlaceholder: "28",
  apaLabel: "APA 7th edition",
  mlaLabel: "MLA 9th edition",
  chicagoLabel: "Chicago 18th edition (notes and bibliography)",
  harvardLabel: "Harvard (Cite Them Right)",
  referenceList: "Reference list",
  parenthetical: "In-text citation (parenthetical)",
  narrative: "In-text citation (narrative)",
  worksCited: "Works Cited",
  inText: "In-text citation",
  footnote: "Footnote",
  shortNote: "Shortened note",
  bibliography: "Bibliography",
  apaNote:
    "APA 7 cites a report by an organization with the organization as author and its publisher after the title. For an SEC filing the company is the author and the SEC the publisher. The year is the year the document was filed or published.",
  mlaNote:
    "MLA 9 names the company as author when someone else publishes the document, as the SEC does. When the company publishes its own annual report, MLA drops the author and begins with the title. The date of a filing is given in full.",
  chicagoNote:
    "Chicago 18 treats a report like a book: organization, italic title, publisher and date in parentheses, then the page. It no longer asks for the place of publication. Library guides differ on a filing's publisher; this names the SEC, which hosts the copy you read.",
  harvardNote:
    "Harvard has many variants. This is Cite Them Right (13th edition), the one most UK universities use: organization and year, italic title, then Available at and the date accessed for an online report. Follow your own institution's variant if it differs.",
  exampleAnnual: {
    company: "Microsoft Corporation",
    title: "Microsoft annual report 2023",
    date: "2023",
    publisher: "Microsoft Corporation",
    url: "https://www.microsoft.com/investor/reports/ar23/index.html",
    accessed: "2024-03-01",
  },
  exampleSec: {
    form: "10-K",
    company: "Apple Inc.",
    period: "2023-09-30",
    date: "2023-11-03",
    url: "https://www.sec.gov/Archives/edgar/data/320193/000032019323000106/aapl-20230930.htm",
    accessed: "2024-03-01",
  },
} as const;

type Strings = typeof ANNUAL_REPORT_CITATION_GENERATOR_STRINGS;

function textField(
  id: string,
  label: string,
  placeholder: string,
  hint?: string
): SourceField {
  return { id, kind: "text", label, placeholder, hint };
}

function formOptions(strings: Strings): readonly Option[] {
  return [
    { value: "10-K", label: strings.form10k },
    { value: "10-Q", label: strings.form10q },
    { value: "8-K", label: strings.form8k },
    { value: "20-F", label: strings.form20f },
  ];
}

function buildFields(strings: Strings, kind: ReportKind): SourceField[] {
  const sec = kind === "sec";
  const formFields: SourceField[] = sec
    ? [
        {
          id: F.form,
          kind: "select",
          label: strings.formLabel,
          options: formOptions(strings),
        },
      ]
    : [];
  const periodFields: SourceField[] = sec
    ? [
        {
          id: F.period,
          kind: "date",
          label: strings.periodLabel,
          placeholder: strings.periodPlaceholder,
        },
      ]
    : [];
  return [
    ...formFields,
    textField(
      F.company,
      strings.companyLabel,
      strings.companyPlaceholder,
      strings.companyHint
    ),
    textField(
      F.title,
      sec ? strings.titleSecLabel : strings.titleAnnualLabel,
      sec ? strings.titleSecPlaceholder : strings.titleAnnualPlaceholder,
      strings.titleHint
    ),
    ...periodFields,
    {
      id: F.date,
      kind: "date",
      label: sec ? strings.dateSecLabel : strings.dateAnnualLabel,
      placeholder: strings.datePlaceholder,
    },
    textField(
      F.publisher,
      strings.publisherLabel,
      strings.publisherPlaceholder
    ),
    {
      id: F.url,
      kind: "url",
      label: strings.urlLabel,
      placeholder: strings.urlPlaceholder,
      wide: true,
    },
    {
      id: F.accessed,
      kind: "date",
      label: strings.accessedLabel,
      placeholder: strings.accessedPlaceholder,
    },
    textField(F.page, strings.pageLabel, strings.pagePlaceholder),
  ];
}

/** The widget's strings, as the formatter's labels. */
export function annualReportLabels(strings: Strings): AnnualReportLabels {
  return {
    apa: strings.apaLabel,
    mla: strings.mlaLabel,
    chicago: strings.chicagoLabel,
    harvard: strings.harvardLabel,
    referenceList: strings.referenceList,
    parenthetical: strings.parenthetical,
    narrative: strings.narrative,
    worksCited: strings.worksCited,
    inText: strings.inText,
    footnote: strings.footnote,
    shortNote: strings.shortNote,
    bibliography: strings.bibliography,
    apaNote: strings.apaNote,
    mlaNote: strings.mlaNote,
    chicagoNote: strings.chicagoNote,
    harvardNote: strings.harvardNote,
  };
}

export default function AnnualReportCitationGeneratorWidget() {
  const strings = useToolUi(ANNUAL_REPORT_CITATION_GENERATOR_STRINGS);
  const [kind, setKind] = useState<ReportKind>("sec");
  const fields = useMemo(() => buildFields(strings, kind), [strings, kind]);
  const labels = useMemo(() => annualReportLabels(strings), [strings]);
  const format = useCallback(
    (values: SourceValues) => formatAnnualReport(kind, values, labels),
    [kind, labels]
  );
  const isReady = useCallback(
    (values: SourceValues) => annualReportIsReady(kind, values),
    [kind]
  );

  return (
    <SourceCitationBuilder
      example={kind === "sec" ? strings.exampleSec : strings.exampleAnnual}
      fields={fields}
      format={format}
      intro={
        <PillGroup
          label={strings.kindLabel}
          name="annual-report-kind"
          onChange={setKind}
          options={[
            { value: "sec", label: strings.kindSec },
            { value: "annual", label: strings.kindAnnual },
          ]}
          value={kind}
        />
      }
      isReady={isReady}
      strings={strings}
    />
  );
}
